import { Component, Input, OnInit, QueryList, ViewChildren } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { map, throwError, catchError, of, forkJoin, Observable } from 'rxjs';
import { MsgModel } from 'src/app/component/common-components/msg/model/msg.model';
import { UserProfileService } from 'src/app/component/user-profile/service/user-profile.service';
import { GetPropertyPipe } from 'src/app/pipes/get-property.pipe';
import { WorkflowStateTypes } from '../../model/workflow-state-types.model';
import { Workflows } from '../../model/workflows.model';
import { WorkflowStatesService } from '../../service/workflow-states.service';
import { WorkflowsService } from '../../service/workflows.service';

@Component({
    selector: 'edit-workflow-states',
    templateUrl: './edit-workflow-states.component.html',
    styleUrls: ['./edit-workflow-states.component.scss']
})
export class EditWorkflowStatesComponent implements OnInit {
    @Input() workflow!: Workflows;

    @ViewChildren(MatPaginator) paginators: QueryList<MatPaginator> = new QueryList<MatPaginator>();
    @ViewChildren(MatSort) sorts: QueryList<MatSort> = new QueryList<MatSort>();

    // Table display
    displayedColumns: string[] = ['EDIT', 'NAME', 'DESCRIPTION', 'ACTION'];
    newDisplayedColumns: string[] = ['ADD'];

    // Table results
    tableDataSources: MatTableDataSource<WorkflowStateTypes>[] = [];

    // Results
    results: WorkflowStateTypes[] = [];
    displayName: string = '';
    filter: string = '';

    isLoading: boolean = false;
    msg: MsgModel = {};

    getPropertyPipe = new GetPropertyPipe();

    // Editing variables
    addingForm!: UntypedFormGroup;
    adding: boolean = false;
    editingForm!: UntypedFormGroup;
    editingId: number | undefined;

    hasPermission: boolean = false;
    permissionIDs: string[] = ['anthony.nguyen', 'lthiru', 'rdiaz2'];

    constructor(
        private fb: UntypedFormBuilder,
        private workflowsService: WorkflowsService,
        private userProfileService: UserProfileService,
        private workflowStatesService: WorkflowStatesService
    ) {
        this.addingForm = fb.group({
            name: ['', [Validators.required]],
            description: ['', [Validators.required]],
            action: ['', [Validators.required]]
        });
        this.editingForm = fb.group({
            name: ['', [Validators.required]],
            description: ['', [Validators.required]],
            action: ['', [Validators.required]]
        });
    }

    ngOnInit(): void {
        this.isLoading = true;

        let obs: Observable<any>[] = [];
        obs.push(this.getWorkflowStateTypes());
        obs.push(this.getPermission());

        forkJoin(obs).subscribe((res) => {
            this.updateDataSource();
            this.isLoading = false;
        });
    }

    getPermission() {
        return this.userProfileService.getMyProfile().pipe(
            map((res: any) => {
                if (res.user) {
                    this.hasPermission = this.permissionIDs.findIndex((id: any) => id === res.user.USER_ID) >= 0;
                    return res;
                } else if (res.msg) {
                    this.msg = res.msg;
                    return res;
                } else {
                    return throwError(() => new Error('Error retrieving User Profile.'));
                }
            }),
            catchError((err) => {
                return of(err);
            })
        );
    }

    getWorkflowStateTypes() {
        return this.workflowsService.getWorkflowStates(this.workflow.ID!).pipe(
            map((res) => {
                if (res.states) {
                    this.results = res.states;
                    return res;
                } else if (res.msg) {
                    this.msg = res.msg;
                    return res;
                } else {
                    return throwError(() => new Error('Error retrieving Workflow State Types.'));
                }
            }),
            catchError((err) => {
                return of(err);
            })
        );
    }

    addNewWorkflowStateType(workflowStateType: WorkflowStateTypes) {
        this.workflowStatesService.createWorkflowState(workflowStateType, this.workflow.ID!).subscribe({
            next: (res) => {
                this.msg = res.msg;
            },
            error: (err) => {
                this.msg = { SUCCESS: false, MSG: 'Error adding new Workflow State Type.' };
            },
            complete: () => {
                this.getWorkflowStateTypes();
            }
        });
    }

    updateWorkflowStateType(workflowStateType: WorkflowStateTypes) {
        this.workflowStatesService.updateWorkflowState(workflowStateType).subscribe({
            next: (res) => {
                this.msg = res.msg;
            },
            error: (err) => {
                this.msg = { SUCCESS: false, MSG: 'Error updating new Workflow State Type.' };
            },
            complete: () => {
                this.getWorkflowStateTypes();
            }
        });
    }

    deleteSelectedWorkflowStateType(workflowStateType: WorkflowStateTypes) {
        this.workflowStatesService.deleteWorkflowState(workflowStateType).subscribe({
            next: (res) => {
                this.msg = res.msg;
            },
            error: (err) => {
                this.msg = { SUCCESS: false, MSG: 'Error deleting new Workflow State Type.' };
            },
            complete: () => {
                this.getWorkflowStateTypes();
            }
        });
    }

    updateDataSource() {
        this.tableDataSources = [];

        setTimeout(() => {
            // Data
            let dataSource = new MatTableDataSource<WorkflowStateTypes>();
            dataSource.data = this.results;

            // Paginator
            //dataSource.paginator = this.paginators.toArray()[0];

            // Sort
            dataSource.sort = this.sorts.toArray()[0];
            const defaultSort: Sort = { active: 'NAME', direction: 'asc' };
            dataSource.sort.active = defaultSort.active;
            dataSource.sort.direction = defaultSort.direction;
            dataSource.sort.sortChange.emit(defaultSort);
            dataSource.sortingDataAccessor = (obj, property) => this.getPropertyPipe.transform(obj, property, true);

            // Filter
            dataSource.filterPredicate = (data: any, filter) => {
                const dataStr = JSON.stringify(data).toLowerCase();
                return dataStr.indexOf(filter) != -1;
            };

            this.tableDataSources.push(dataSource);
        });
    }

    setFilter() {
        this.tableDataSources.forEach((dataSource, index) => {
            dataSource.filter = this.filter.trim().toLocaleLowerCase();
        });
    }

    // Add Function
    addWorkflowStateType() {
        this.newDisplayedColumns = ['ADD_EDIT', 'ADD_NAME', 'ADD_DESCRIPTION', 'ADD_ACTION'];
        this.resetAddingWorkflowStateType();
        this.adding = true;
    }
    confirmAddWorkflowStateType() {
        this.addNewWorkflowStateType(this.buildWorkflowStateType(this.addingForm));
        this.cancelAdd();
    }
    cancelAddWorkflowStateType() {
        this.cancelAdd();
    }

    // Edit Function
    editWorkflowStateType(workflowStateType: WorkflowStateTypes) {
        this.setEditingWorkflowStateType(workflowStateType);
    }
    confirmEditWorkflowStateType() {
        this.updateWorkflowStateType(this.buildWorkflowStateType(this.editingForm));
        this.cancelEdit();
    }
    cancelEditWorkflowStateType() {
        this.cancelEdit();
    }

    // Delete Function
    deleteWorkflowStateType(workflowStateType: WorkflowStateTypes) {
        this.deleteSelectedWorkflowStateType(workflowStateType);
    }

    // Reset Function
    private cancelAdd() {
        this.resetAddingWorkflowStateType();
        this.newDisplayedColumns = ['ADD'];
        this.adding = false;
    }

    private cancelEdit() {
        this.resetEditingWorkflowStateType();
    }

    private setEditingWorkflowStateType(workflowStateType: WorkflowStateTypes) {
        this.editingId = workflowStateType.ID;
        this.editingForm.patchValue({ name: workflowStateType.NAME, description: workflowStateType.DESCRIPTION, action: workflowStateType.ACTION });
        this.editingForm.markAsPristine();
    }

    private resetAddingWorkflowStateType() {
        this.addingForm.patchValue({ name: '', description: '', action: '' });
        this.addingForm.markAsPristine();
    }

    private resetEditingWorkflowStateType() {
        this.editingForm.patchValue({ name: '', description: '', action: '' });
        this.editingId = undefined;
    }

    private buildWorkflowStateType(form: UntypedFormGroup): WorkflowStateTypes {
        let newWorkflowStateType = new WorkflowStateTypes();

        newWorkflowStateType.ID = this.editingId ? this.editingId : undefined;
        newWorkflowStateType.NAME = form.get('name')?.value;
        newWorkflowStateType.DESCRIPTION = form.get('description')?.value;
        newWorkflowStateType.ACTION = form.get('action')?.value;

        return newWorkflowStateType;
    }
}
